import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'

import { FieldLabel } from '@/components/FieldLabel'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { RolesApi, type RoleDetail } from '@/services/users/rolesApi'

interface CopyRoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  role: RoleDetail
}

/** Creates a new role with another role's application, scope and permissions. */
export function CopyRoleDialog({ open, onOpenChange, role }: CopyRoleDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [name, setName] = useState(t('copyRole.copyName', { name: role.name }))
  const [description, setDescription] = useState(role.description)
  const [openCopy, setOpenCopy] = useState(true)

  const mutation = useMutation({
    mutationFn: () =>
      RolesApi.create(crypto.randomUUID().replace(/-/g, '').slice(0, 16), {
        name: name.trim(),
        description,
        application: role.application,
        scope: role.scope,
        permissions: role.permissions,
        objectState: 'ACTIVE',
      }),
    onSuccess: (copy) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast.success(t('copyRole.copied'), {
        description: t('copyRole.copiedDetail', { name: copy.name }),
        duration: 3000,
      })
      onOpenChange(false)
      if (openCopy) navigate(`/admin/roles/${copy.id}`)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t('copyRole.failed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const valid = !!name.trim()

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) mutation.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('copyRole.title')}</DialogTitle>
            <DialogDescription>{t('copyRole.description', { name: role.name })}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="copy-role-name">{t('common.name')}</FieldLabel>
            <Input
              id="copy-role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="copy-role-description">{t('common.description')}</FieldLabel>
            <Input
              id="copy-role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="copy-role-open"
              checked={openCopy}
              onCheckedChange={(v) => setOpenCopy(v === true)}
            />
            <Label htmlFor="copy-role-open" className="font-normal">
              {t('copyRole.openCopy')}
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={!valid}>
              <Copy className="size-4" />
              {t('copyRole.copy')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
